import React from 'react';
import { MessageCircle, Users, Building2, Shield, QrCode, Zap, Clock, CheckCircle2, ArrowRight, GraduationCap, Heart } from 'lucide-react';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-cream-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-sage-100">
        <div className="max-w-6xl mx-auto px-6">
          <nav className="flex items-center justify-between h-16 md:h-20">
            <a href="/" className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-sage-100">
                <GraduationCap className="w-6 h-6 text-sage-700" />
              </div>
              <span className="font-serif text-xl font-bold text-gray-900">Wisuda.ai</span>
            </a>
            <button className="px-5 py-2.5 bg-sage-600 hover:bg-sage-700 text-white text-sm font-semibold rounded-full transition-all duration-300 shadow-md hover:shadow-lg">
              Coba Demo
            </button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-cream-50 via-cream-100 to-sage-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Satu Kanal Informasi Resmi<br />untuk Seluruh Peserta Wisuda
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-6 leading-relaxed">
            Wisuda.ai membantu kampus menyampaikan informasi wisuda secara terpusat, 
            konsisten, dan real-time melalui AI berbasis WhatsApp.
          </p>
          <p className="text-lg text-gray-600 mb-10">
            Tidak perlu aplikasi tambahan. Tidak membebani panitia. Tetap menjaga etika akademik.
          </p>
          <button className="group px-8 py-4 bg-sage-600 hover:bg-sage-700 text-white font-semibold rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
            <span className="flex items-center gap-2">
              Coba Demo
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
          <p className="mt-4 text-sm text-gray-600">
            Lihat langsung bagaimana AI menjawab pertanyaan peserta wisuda
          </p>
        </div>
      </section>

      {/* Problems */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Masalah Utama di Acara Wisuda
          </h2>
          <p className="text-lg text-gray-700 mb-12 text-center max-w-3xl mx-auto">
            Dalam acara wisuda dengan ratusan hingga ribuan peserta, informasi sering kali:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Tersebar di banyak grup dan kanal',
              'Ditanyakan berulang kali kepada panitia',
              'Tidak diterima serentak oleh seluruh peserta',
              'Berpotensi menimbulkan kebingungan di hari-H'
            ].map((problem, index) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-terracotta-50 rounded-xl border border-terracotta-100">
                <div className="w-8 h-8 bg-terracotta-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-terracotta-700 font-bold">{index + 1}</span>
                </div>
                <p className="text-gray-800 text-lg">{problem}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 p-6 bg-terracotta-100 rounded-xl text-center">
            <p className="text-gray-800">
              Akibatnya, panitia kelelahan menjawab pertanyaan administratif, dan peserta tidak mendapatkan pengalaman wisuda yang nyaman.
            </p>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-20 px-6 bg-gradient-to-br from-sage-50 to-cream-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Solusi: Wisuda.ai sebagai Pusat Informasi
          </h2>
          <p className="text-xl text-gray-700 mb-12 text-center max-w-3xl mx-auto">
            Wisuda.ai berfungsi sebagai <span className="font-bold text-sage-700">AI Agent resmi acara wisuda</span> yang 
            menjadi satu-satunya kanal informasi terverifikasi.
          </p>
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-sage-200">
            <p className="text-lg text-gray-700 mb-6 text-center">
              Melalui satu percakapan WhatsApp, peserta dapat:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: Clock, text: 'Mengakses jadwal dan alur prosesi' },
                { icon: CheckCircle2, text: 'Mengetahui tata tertib wisuda' },
                { icon: Zap, text: 'Menerima pengumuman hari-H secara real-time' },
                { icon: MessageCircle, text: 'Bertanya tanpa membebani panitia' }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-sage-50 rounded-lg">
                  <item.icon className="w-5 h-5 text-sage-600 flex-shrink-0" />
                  <span className="text-gray-800">{item.text}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-center text-sage-700 font-medium">
              Semua informasi bersumber dari dan disetujui oleh kampus.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Bagaimana Wisuda.ai Bekerja
          </h2>
          <div className="space-y-6">
            {[
              { icon: Building2, text: 'Kampus menyiapkan informasi resmi acara wisuda' },
              { icon: QrCode, text: 'Panitia menyediakan QR Code Wisuda.ai' },
              { icon: MessageCircle, text: 'Peserta scan QR dan terhubung ke WhatsApp' },
              { icon: Zap, text: 'AI menyampaikan informasi secara otomatis dan konsisten' }
            ].map((step, index) => (
              <div key={index} className="flex items-center gap-6 p-6 bg-cream-50 rounded-xl border border-cream-200">
                <div className="w-12 h-12 bg-sage-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">{index + 1}</span>
                </div>
                <div className="flex items-center gap-4">
                  <step.icon className="w-6 h-6 text-sage-600" />
                  <p className="text-gray-800 text-lg">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-gray-700 font-medium">
            Tidak ada aplikasi tambahan. Tidak ada komunikasi manual berulang.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-6 bg-gradient-to-br from-cream-100 to-sage-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Dampak Langsung bagi Kampus & Panitia
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-md border border-sage-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-sage-100 rounded-xl">
                  <Building2 className="w-6 h-6 text-sage-700" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Untuk Kampus</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Informasi lebih tertib dan terkontrol',
                  'Narasi resmi tersampaikan konsisten',
                  'Reputasi institusi tetap terjaga'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-sage-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-md border border-terracotta-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-terracotta-100 rounded-xl">
                  <Users className="w-6 h-6 text-terracotta-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Untuk Panitia & EO</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Pertanyaan berulang berkurang drastis',
                  'Koordinasi lapangan lebih ringan',
                  'Fokus pada prosesi akademik'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-terracotta-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Kenapa Melalui WhatsApp?
          </h2>
          <p className="text-lg text-gray-700 mb-10">Karena WhatsApp adalah kanal yang:</p>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              'Sudah digunakan oleh hampir semua peserta',
              'Mudah diakses tanpa pelatihan',
              'Cepat, personal, dan real-time',
              'Cocok untuk komunikasi acara berskala besar'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-sage-50 rounded-lg border border-sage-200">
                <CheckCircle2 className="w-5 h-5 text-sage-600 flex-shrink-0" />
                <span className="text-gray-800">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-gray-700">
            Wisuda.ai memanfaatkan kebiasaan digital mahasiswa tanpa mengubah alur kerja panitia.
          </p>
        </div>
      </section>

      {/* Privacy */}
      <section className="py-20 px-6 bg-gradient-to-br from-sage-50 to-cream-100">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Shield className="w-6 h-6 text-sage-700" />
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900">
              Komitmen Privasi & Etika
            </h2>
          </div>
          <p className="text-lg text-gray-700 mb-10 text-center">
            Wisuda.ai dirancang khusus untuk lingkungan akademik:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              'Non-komersial terhadap mahasiswa',
              'Data hanya aktif selama acara',
              'Seluruh data diserahkan & dihapus setelah wisuda',
              'Tidak mengumpulkan data akademik'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-white rounded-lg border border-sage-200">
                <Shield className="w-5 h-5 text-sage-600 flex-shrink-0" />
                <span className="text-gray-800">{item}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-sage-700 font-semibold text-lg">
            Wisuda.ai adalah alat bantu kampus, bukan media promosi.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-sage-600 to-sage-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
            Siap Menyatukan Informasi Wisuda<br />dalam Satu Kanal Resmi?
          </h2>
          <p className="text-xl text-sage-100 mb-10">
            Rasakan langsung bagaimana Wisuda.ai membantu kampus menyampaikan informasi wisuda secara tertib dan real-time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-white hover:bg-cream-50 text-sage-700 font-bold rounded-full transition-all duration-300 shadow-lg">
              Coba Demo Wisuda.ai
            </button>
            <button className="px-8 py-4 bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold rounded-full transition-all duration-300">
              Hubungi Kami
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-cream-200 border-t border-sage-200">
        <div className="max-w-6xl mx-auto text-center">
          <h3 className="font-serif text-3xl font-bold text-gray-900 mb-4">Wisuda.ai</h3>
          <p className="text-gray-700 mb-6">
            Satu kanal informasi resmi untuk seluruh peserta wisuda.
          </p>
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <span>Dibuat dengan</span>
            <Heart className="w-4 h-4 text-terracotta-500 fill-current" />
            <span>untuk pendidikan Indonesia</span>
          </div>
          <div className="mt-8 pt-8 border-t border-sage-200">
            <p className="text-sm text-gray-600">© 2026 PT AI Indonesia. Semua hak dilindungi.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
